import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  LogIn,
  LogOut,
  MessageSquare,
  ThumbsUp,
  FileText,
  LayoutDashboard,
  Globe,
  Shield,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import {
  adminLogin,
  getAdminToken,
  getAdminEmail,
  clearAdminSession,
  changePassword,
} from '@/api/admin';

import ThreadsTab from '@/components/admin/ThreadsTab';
import FeedbackTab from '@/components/admin/FeedbackTab';
import DocumentsTab from '@/components/admin/DocumentsTab';
import DailyStatsTab from '@/components/admin/DailyStatsTab';
import SiteSyncTab from '@/components/admin/SiteSyncTab';
import AdminUsersTab from '@/components/admin/AdminUsersTab';

// ==================== Change Password Dialog ====================

function ChangePasswordDialog({
  open,
  forced,
  onClose,
}: {
  open: boolean;
  forced: boolean;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await changePassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !forced) onClose();
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}
        className={forced ? '[&>button:last-child]:hidden' : ''}
      >
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            {forced
              ? 'You must change your temporary password before continuing.'
              : 'Enter a new password for your admin account.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 8 characters"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            {!forced && (
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Login Form ====================

function AdminLogin({ onLogin }: { onLogin: (mustChange: boolean) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await adminLogin(email.trim(), password);
      if (result.success) {
        onLogin(result.must_change_password ?? false);
      } else {
        setError('Invalid email or password.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle>Admin Dashboard</CardTitle>
          <CardDescription>Sign in with your admin credentials</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Main Admin Page ====================

export default function Admin() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(!!getAdminToken());
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [forceChangePassword, setForceChangePassword] = useState(false);

  const handleLogout = () => {
    clearAdminSession();
    setAuthenticated(false);
    setShowChangePassword(false);
    setForceChangePassword(false);
  };

  useEffect(() => {
    const onExpired = () => handleLogout();
    const onForcePasswordChange = () => {
      setForceChangePassword(true);
      setShowChangePassword(true);
    };
    window.addEventListener('admin-session-expired', onExpired);
    window.addEventListener('admin-force-password-change', onForcePasswordChange);
    return () => {
      window.removeEventListener('admin-session-expired', onExpired);
      window.removeEventListener('admin-force-password-change', onForcePasswordChange);
    };
  }, []);

  const handleLogin = (mustChange: boolean) => {
    setAuthenticated(true);
    if (mustChange) {
      setForceChangePassword(true);
      setShowChangePassword(true);
    }
  };

  const handlePasswordChanged = () => {
    setShowChangePassword(false);
    setForceChangePassword(false);
  };

  if (!authenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <ChangePasswordDialog
        open={showChangePassword}
        forced={forceChangePassword}
        onClose={handlePasswordChanged}
      />

      <header className="border-b border-border/70 bg-card/75">
        <div className="container flex h-16 items-center justify-between gap-4 px-4 mx-auto">
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-[-0.025em]">
              IPMA Copilot
            </p>
            <p className="text-xs text-muted-foreground">Admin workspace</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {getAdminEmail()}
            </span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Chat
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container max-w-6xl px-4 py-6 mx-auto">
        <Tabs defaultValue="stats">
          <TabsList className="h-auto w-full justify-start gap-0.5 overflow-x-auto rounded-xl border border-border/70 bg-card p-1 sm:w-fit">
            <TabsTrigger value="stats" className="gap-1.5">
              <LayoutDashboard className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="threads" className="gap-1.5">
              <MessageSquare className="h-4 w-4" />
              Chat Threads
            </TabsTrigger>
            <TabsTrigger value="feedback" className="gap-1.5">
              <ThumbsUp className="h-4 w-4" />
              Feedback
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-1.5">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="site-sync" className="gap-1.5">
              <Globe className="h-4 w-4" />
              Site Sync
            </TabsTrigger>
            <TabsTrigger value="admin-users" className="gap-1.5">
              <Shield className="h-4 w-4" />
              Admin Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stats">
            <DailyStatsTab />
          </TabsContent>
          <TabsContent value="threads">
            <ThreadsTab />
          </TabsContent>
          <TabsContent value="feedback">
            <FeedbackTab />
          </TabsContent>
          <TabsContent value="documents">
            <DocumentsTab />
          </TabsContent>
          <TabsContent value="site-sync">
            <SiteSyncTab />
          </TabsContent>
          <TabsContent value="admin-users">
            <AdminUsersTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
